// src/pages/PatientCarePlanPage.jsx
// Route: /patient/care-plan
//
// Patient-facing view of navigator-assigned action items.
// Read-only — patients can see their care plan but cannot modify it.
//
// API: GET /api/patients/{patientId}/care-plan
// Returns items sorted newest-first, includes completed items.
//
// Gap Report — PatientCarePlanPage.jsx
// ✅ Confirmed: read-only action items list, priority badges, completion state
// ⚠️ Assumptions: No Figma frame. Follows PatientLayout + existing page patterns.
// ❌ Missing Figma frames: All states. Fallback: consistent with PatientHealthTimeline.jsx.
// 🔗 Existing: PatientLayout, usePatient(), useToast(), design tokens
// 🚫 Deviations: None

import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PatientAppShell } from '../components/PatientLayout';
import PageHead from '../components/PageHead';
import MyHealthTabs from '../components/MyHealthTabs';
import EmptyState from '../components/EmptyState';
import { usePatient } from '../context/PatientContext';
import Icon from '../components/Icon';
import { filterCarePlanItems } from '../utils/carePlanItems';
import { isLabTestSuggestion, LAB_TEST_ORDER_PATH } from '../utils/labTestSuggestion';

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

// Test runs (automated and manual) write action items against real patient
// records and tag them "[TEST]" — "[TEST] Toggle round-trip", "[TEST] Automated
// test prescription item — safe to delete" — and nothing cleans them up. A
// patient whose record has been used for testing opened this page and read 44
// of them mixed in with their actual care.
//
// CarePlanAssembler now drops them server-side, which is where the rule belongs
// (every other consumer of the care plan gets it too). This guard is here only
// so the patient view is clean against an API that has not been deployed yet,
// and so a marker that reaches the client some other way still never renders as
// care. It is a marker check, not a parser — the clinical text still arrives
// ready to render.
//
// The filter itself (isTestItem/isBlankDoctorNote) now lives in
// utils/carePlanItems.js, shared with patientSummary.js — its
// openActionItems used to count every item the API returned, test debris
// included, so the Today screen's attention banner could show a pending
// count higher than the Open count this page shows for the same patient.

// ── Priority styling ──────────────────────────────────────────────────────

const PRIORITY_CONFIG = {
  URGENT:  { label: 'Urgent',  bg: 'var(--red-bg)',    color: 'var(--red)',          icon: '🚨' },
  HIGH:    { label: 'High',    bg: 'var(--amber-bg)',  color: 'var(--amber)',        icon: '⚠️' },
  MEDIUM:  { label: 'Medium',  bg: 'var(--blue-bg)',   color: 'var(--blue)',         icon: '📋' },
  LOW:     { label: 'Low',     bg: 'var(--bg-subtle)', color: 'var(--text-muted)',   icon: '📝' },
  ROUTINE: { label: 'Routine', bg: 'var(--bg-subtle)', color: 'var(--text-muted)',   icon: '📝' },
};

function PriorityBadge({ priority }) {
  const cfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.ROUTINE;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '3px 9px',
      borderRadius: 20, letterSpacing: '0.05em', textTransform: 'uppercase',
      background: cfg.bg, color: cfg.color,
      fontFamily: 'var(--font-body)',
    }}>
      {cfg.label}
    </span>
  );
}

// ── Author styling ────────────────────────────────────────────────────────
//
// Who to credit an item to — a doctor writing it, a navigator, or the
// triage/action-plan engine. Deliberately a plain outlined pill rather than
// PriorityBadge's filled colour: this isn't a severity signal, so it
// shouldn't compete with URGENT/HIGH for the patient's attention.
const AUTHOR_CONFIG = {
  DOCTOR:    { label: 'Doctor',    icon: '🩺' },
  NAVIGATOR: { label: 'Navigator', icon: '🧭' },
  AI:        { label: 'AI',        icon: '🤖' },
};

function AuthorBadge({ authorRole }) {
  const cfg = AUTHOR_CONFIG[authorRole];
  if (!cfg) return null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 11, fontWeight: 500, padding: '3px 9px',
      borderRadius: 20, border: '1px solid var(--border)',
      color: 'var(--text-secondary)', fontFamily: 'var(--font-body)',
    }}>
      <span aria-hidden="true">{cfg.icon}</span>
      {cfg.label}
    </span>
  );
}

// The doctor behind an item: "Approved by Dr. Priya Nair" when a doctor wrote it or accepted
// an AI draft (the server only sends approvedByName in those cases), otherwise — for an
// AI-generated item — an honest "not yet reviewed by a doctor". Navigator-added items say
// nothing (their badge already names the navigator).
function ApprovedBy({ item }) {
  const base = { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontFamily: 'var(--font-body)' };
  if (item.approvedByName) {
    const when = item.approvedAt
      ? ` · ${new Date(item.approvedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}`
      : '';
    return (
      <span style={{ ...base, color: 'var(--green)', fontWeight: 500 }}>
        <span aria-hidden="true">✓</span> Approved by {item.approvedByName}{when}
      </span>
    );
  }
  if (item.authorRole === 'AI') {
    return <span style={{ ...base, color: 'var(--text-muted)' }}>Not yet reviewed by a doctor</span>;
  }
  return null;
}

function CreatedDate({ date }) {
  if (!date) return null;
  return (
    <span className="careplan-card__date" style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 12, fontFamily: 'var(--font-body)', color: 'var(--text-muted)',
    }}>
      <Icon name="calendar" size={13} />
      Added {new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
    </span>
  );
}

// AI-generated items bundle every finding from the same report into one
// description — "Troponin critically elevated — possible acute MI. BNP
// critically elevated — acute heart failure risk. eGFR critically low —
// urgent renal review." — which fills a whole card with a wall of text
// where a doctor/navigator note is a single short sentence. Splits on ". "
// (the punctuation the server already writes between findings); a
// single-sentence description splits to just itself, so this only changes
// anything for the multi-finding case.
function splitFindings(description) {
  if (!description) return [];
  return description.split(/(?<=\.)\s+/).map(s => s.trim()).filter(Boolean);
}

// The short marker/status naming a finding, dropping the clarifying clause
// after its em dash — "Troponin critically elevated — possible acute MI."
// becomes "Troponin critically elevated". Falls back to the whole sentence
// when there's no dash to split on.
function shortFinding(sentence) {
  const dashIdx = sentence.indexOf('—');
  const short = dashIdx > -1 ? sentence.slice(0, dashIdx) : sentence;
  return short.replace(/\.\s*$/, '').trim();
}

function DueDate({ date }) {
  if (!date) return null;
  const due   = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const overdue = due < today;
  const soon    = !overdue && (due - today) / 86400000 <= 7;

  // The glyph inherits the span's colour, so overdue is red, due-soon amber and
  // everything else muted — an emoji stayed its own colour and fought the state
  // it was supposed to be signalling.
  return (
    <span className="careplan-card__date" style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 12, fontFamily: 'var(--font-body)',
      color: overdue ? 'var(--red)' : soon ? 'var(--amber-text)' : 'var(--text-muted)',
    }}>
      <Icon name={overdue ? 'alert' : soon ? 'clock' : 'calendar'} size={13} />
      {overdue ? 'Overdue · ' : soon ? 'Due soon · ' : ''}
      {due.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
    </span>
  );
}

function ActionItemCard({ item }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const cfg = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG.ROUTINE;
  const findings = useMemo(() => splitFindings(item.description), [item.description]);
  const isSuggestion = isLabTestSuggestion(item.description);
  const hasMultiple = findings.length > 1;
  const additionalCount = findings.length - 1;
  const summary = hasMultiple ? `${shortFinding(findings[0])} +${additionalCount}` : item.description;

  return (
    // .careplan-card (index.css): on desktop, height: 100% + its own scroll —
    // fills the carousel's fixed-height slot instead of sitting at natural
    // (often much shorter) content height inside it, and a long description
    // scrolls within its own card instead of dwarfing the other two in the
    // row. On phones the card grows to fit and never scrolls.
    <div className="careplan-card" style={{
      background: 'var(--bg-surface)',
      border: `1px solid ${item.completed ? 'var(--border)' : cfg.bg}`,
      borderLeft: `4px solid ${item.completed ? 'var(--border)' : cfg.color}`,
      borderRadius: 10, padding: '16px 18px',
      opacity: item.completed ? 0.7 : 1,
      transition: 'opacity 0.2s',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
        {/* Completion indicator */}
        <div style={{
          width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
          border: `2px solid ${item.completed ? 'var(--green)' : cfg.color}`,
          background: item.completed ? 'var(--green-bg)' : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginTop: 1,
        }}>
          {item.completed && (
            <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
              <path d="M1 4L3.5 6.5L9 1" stroke="var(--green)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            onClick={hasMultiple ? (() => setExpanded(v => !v)) : undefined}
            role={hasMultiple ? 'button' : undefined}
            tabIndex={hasMultiple ? 0 : undefined}
            aria-expanded={hasMultiple ? expanded : undefined}
            onKeyDown={hasMultiple ? (e => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(v => !v); }
            }) : undefined}
            style={{
              margin: '0 0 8px', fontSize: 14, fontWeight: 500,
              color: item.completed ? 'var(--text-muted)' : 'var(--text-primary)',
              fontFamily: 'var(--font-body)', lineHeight: 1.6,
              textDecoration: item.completed ? 'line-through' : 'none',
              cursor: hasMultiple ? 'pointer' : 'default',
            }}
          >
            {/* Rendered verbatim (or, when this item bundles several
                findings, a short "worst finding +N" summary that expands to
                the full text on click — see splitFindings/shortFinding
                above). The server strips its own "[AUTO] triage:… |
                ACUTE ALERT | HIGH |" metadata now — see ActionItemDescription and
                CarePlanAssembler on the backend.
                This used to parse that format here, in the JSX, which put a
                parser for a SERVER storage format in one client view. Every other
                consumer of the same field either reimplemented it or leaked the
                metadata to a human, and content-based de-duplication was
                impossible server-side because the clinical text was only ever
                recovered in the browser — which is how DEF-013's three identical
                cards got through (three different triage ids, one identical
                instruction). */}
            {expanded || !hasMultiple ? item.description : summary}
            {hasMultiple && (
              <Icon name="chevronRight" size={12} style={{
                marginLeft: 6, color: 'var(--text-muted)', verticalAlign: 'middle',
                transition: 'transform 0.15s', transform: expanded ? 'rotate(90deg)' : 'none',
              }} />
            )}
          </p>

          {/* A doctor's lab-test suggestion: one tap to the order flow. */}
          {isSuggestion && !item.completed && (
            <button type="button" className="btn btn-accent btn-sm careplan-card__order" style={{ margin: '0 0 10px' }}
              onClick={() => navigate(LAB_TEST_ORDER_PATH)}>
              Order these tests
            </button>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <PriorityBadge priority={item.priority} />
            <AuthorBadge authorRole={item.authorRole} />
            <ApprovedBy item={item} />
            <CreatedDate date={item.createdAt} />
            <DueDate date={item.dueDate} />
            {item.completed && (
              <span style={{ fontSize: 12, color: 'var(--green)', fontFamily: 'var(--font-body)' }}>
                ✓ Completed
              </span>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

/**
 * Action items as a horizontally-scrolling strip rather than an unbounded
 * vertical list — same reasoning as the Care Team member strip: only a
 * couple of cards are ever fully in view, the rest reachable by scrolling
 * right, so a long care plan doesn't turn this page into one long scroll
 * of its own.
 *
 * 3-up on a phone-width card (this page's only real audience — see
 * PatientAppShell) left each slot ~60px of text width after its padding
 * and completion-circle icon, wrapping "High risk triage — physician
 * review recommendation" to one or two words per line and reading as
 * truncated/broken. .careplan-carousel-item, not an inline flex-basis —
 * .careplan-stats-row above hit the exact hazard an inline style here
 * would repeat (an old unscoped substring-matching mobile rule elsewhere
 * in index.css). 2-up matches the Chapters row's already-legible width one
 * section up on this same screen family (PatientTodayView's ChapterCard).
 *
 * That is desktop only now. 2-up on a 390px phone still left each card ~99px
 * of text width, so at max-width: 768px the strip becomes a single column of
 * full-width cards that grow to fit — no sideways scrolling, no scrolling
 * inside a card. The layout lives in index.css (.careplan-carousel*), where
 * a breakpoint can change it.
 */
function ActionItemsCarousel({ items }) {
  return (
    <div className="careplan-carousel">
      {items.map(item => (
        // On desktop the slot's fixed height (ActionItemCard fills it and
        // scrolls its own overflow) is load-bearing: without it, a merged
        // multi-finding description next to a one-line "Schedule a
        // follow-up" left the row towering over a short card that sat, tiny,
        // at the top of its own much-taller flex-stretched slot — a huge dead
        // gap under anything short. Stacked on phones there is no row to
        // match, so the height is dropped there.
        <div key={item.id} className="careplan-carousel-item">
          <ActionItemCard item={item} />
        </div>
      ))}
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────

/**
 * Was a hand-rolled card: a 📋 emoji, its own type scale, its own border and
 * radius. It was the BEST-looking of the portal's empty states, which is
 * exactly the problem — the shared EmptyState had to be brought up to this
 * standard so the other five screens could match it, rather than this one
 * staying ahead alone.
 */
function EmptyCarePlan() {
  const navigate = useNavigate();
  return (
    <EmptyState
      framed
      icon="clipboard"
      title="No care plan yet"
      message="Your care plan appears here once your navigator or doctor has reviewed
               your reports and assigned action items."
      action={{ label: 'Ask Previu about next steps', onClick: () => navigate('/patient/chat') }}
      hint="Upload a blood test report to get your first review started."
    />
  );
}

// ── Skeleton loader ───────────────────────────────────────────────────────

function Skeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {[1, 2, 3].map(i => (
        <div key={i} style={{
          height: 88, borderRadius: 10,
          background: 'var(--bg-subtle)', animation: 'pulse 1.5s ease-in-out infinite',
        }} />
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function PatientCarePlanPage() {
  const { patient }           = usePatient();
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [filter, setFilter]   = useState('open'); // open | completed | all
  const fetchedRef            = useRef(false);

  useEffect(() => {
    if (!patient?.id || fetchedRef.current) return;
    fetchedRef.current = true;

    async function fetchCarePlan() {
      try {
        const token = localStorage.getItem('previu_token');
        const res   = await fetch(
          `${API_BASE}/patients/${patient.id}/care-plan`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        // Filtered before it reaches state, so the Open/Done/Total counts and
        // the filter tabs are all counting real care items only.
        setItems(filterCarePlanItems(data));
      } catch (e) {
        setError('Could not load your care plan. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    fetchCarePlan();
  }, [patient?.id]);

  const filtered = items.filter(item => {
    if (filter === 'open')      return !item.completed;
    if (filter === 'completed') return item.completed;
    return true;
  });

  const openCount      = items.filter(i => !i.completed).length;
  const completedCount = items.filter(i =>  i.completed).length;

  return (
    <PatientAppShell breadcrumbs={[{ label: 'My Health', to: '/patient/timeline' }, { label: 'Care Plan' }]}>
      <div className="page-shell page-shell--health">
        <MyHealthTabs />
        <PageHead
          eyebrow="Your health journey"
          title="My Care Plan"
          subtitle="Action items assigned by your health navigator and doctor."
        />

        {/* Stats row.
            .careplan-stats-row, not an inline gridTemplateColumns style —
            index.css has an old, unscoped mobile rule written for the
            Doctors page (`div[style*="grid-template-columns: repeat(3,
            1fr)"] { grid-template-columns: 1fr !important; }`) that matches
            ANY div anywhere with that literal inline-style substring, not
            just the one it was meant for. This row used exactly that
            string, so on a phone it silently collapsed to one column —
            "5 OPEN" / "39 DONE" / "44 TOTAL" stacked instead of sitting on
            one row. A dedicated class sidesteps the substring match
            entirely, the same fix PatientAppointmentsPage.jsx's .appt-*
            classes used for the same class of bug. */}
        <style>{`
          .careplan-stats-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 20px; }
        `}</style>
        {!loading && items.length > 0 && (
          <div className="careplan-stats-row">
            {[
              { label: 'Open', value: openCount,      color: 'var(--red)'   },
              { label: 'Done', value: completedCount, color: 'var(--green)' },
              { label: 'Total', value: items.length,  color: 'var(--text-primary)' },
            ].map(s => (
              <div key={s.label} style={{
                background: 'var(--bg-surface)', borderRadius: 10, padding: '14px 16px',
                border: '1px solid var(--border)', textAlign: 'center',
              }}>
                <div style={{
                  fontSize: 26, fontWeight: 700, color: s.color,
                  fontFamily: 'var(--font-mono)', lineHeight: 1,
                }}>
                  {s.value}
                </div>
                <div style={{
                  fontSize: 11, color: 'var(--text-muted)', marginTop: 4,
                  fontFamily: 'var(--font-body)', textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Filter tabs */}
        {!loading && items.length > 0 && (
          <div style={{
            display: 'flex', gap: 6, marginBottom: 16,
          }}>
            {[
              { key: 'open',      label: `Open (${openCount})` },
              { key: 'completed', label: `Done (${completedCount})` },
              { key: 'all',       label: 'All' },
            ].map(tab => (
              <button
                key={tab.key} type="button"
                onClick={() => setFilter(tab.key)}
                style={{
                  padding: '6px 14px', borderRadius: 20, cursor: 'pointer',
                  fontSize: 12, fontWeight: filter === tab.key ? 600 : 400,
                  fontFamily: 'var(--font-body)',
                  border: `1.5px solid ${filter === tab.key ? 'var(--brand-dark)' : 'var(--border)'}`,
                  background: filter === tab.key ? 'var(--brand-dark)' : 'var(--bg-surface)',
                  // --text-on-brand-dark, not --text-inverse: --text-inverse
                  // flips per theme (white in light, near-black in dark) so
                  // it reads fine in light theme but pairs a near-black
                  // color with this same near-black --brand-dark background
                  // in dark theme — invisible active-tab label. Same bug
                  // shape as ChatWindow/MessageBubble/HumanChatWindow this
                  // session; --text-on-brand-dark is always white, matching
                  // a background that's always dark.
                  color: filter === tab.key ? 'var(--text-on-brand-dark)' : 'var(--text-secondary)',
                  transition: 'all 0.15s',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        {loading && <Skeleton />}

        {error && !loading && (
          <div role="alert" style={{
            padding: '16px', borderRadius: 10, fontSize: 13,
            background: 'var(--red-bg)', color: 'var(--red)',
            fontFamily: 'var(--font-body)',
          }}>
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && <EmptyCarePlan />}

        {!loading && !error && items.length > 0 && filtered.length === 0 && (
          <div style={{
            textAlign: 'center', padding: '32px', color: 'var(--text-muted)',
            fontSize: 13, fontFamily: 'var(--font-body)',
          }}>
            No {filter} items.
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <ActionItemsCarousel items={filtered} />
        )}

        {/* Chat CTA at bottom */}
        {!loading && !error && items.length > 0 && (
          <div className="careplan-ask">
            <div style={{ flexShrink: 0, color: 'var(--brand-accent)', display: 'flex' }}>
          <Icon name="chat" size={22} />
        </div>
            <div className="careplan-ask__text">
              <div style={{
                fontWeight: 600, fontSize: 13, color: 'var(--text-primary)',
                fontFamily: 'var(--font-body)', marginBottom: 3,
              }}>
                Questions about your care plan?
              </div>
              <div style={{
                fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'var(--font-body)',
              }}>
                Ask Previu to explain any item or help you take the next step.
              </div>
            </div>
            <a href="/patient/chat" className="careplan-ask__link">
              💬 Ask Previu
            </a>
          </div>
        )}
      </div>
    </PatientAppShell>
  );
}
